import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import TSTnBaziResult from "../components/TSTnBaziResult";
import FortuneResult from "../components/FortuneResult";
import AuthPage from "../pages/AuthPage";
import { supabase } from "../utils/supabaseClient";
import { useLanguage } from "../contexts/LanguageContext";
import { DateTime } from "luxon";
import { analyzeBazi } from "../api/bazi";
import backArrowIcon from '../styles/back-arrow.png';

// 1. Accept 'session' as a prop
export default function ResultsPage({ session }) {
  const { language, setLanguage } = useLanguage();
  const [resultsData, setResultsData] = useState({
    trueSolarTime: null,
    baziPillars: null,
    prediction: "",
    language: "en",
    formData: null
  });
  const [isLoading, setIsLoading] = useState(true);
  
  // 2. State for Auth Modal
  const [showAuth, setShowAuth] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const closeDropdown = (e) => {
      if (showDropdown && !e.target.closest('.auth-top')) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('click', closeDropdown);
    return () => document.removeEventListener('click', closeDropdown);
  }, [showDropdown]);

  const parsePillar = (pillarString) => {
    if (!pillarString || pillarString.length < 2) {
      return { stem: '', branch: '' };
    }
    if (pillarString.includes('(')) {
      const parenIndex = pillarString.indexOf('(');
      const pinyinPart = pillarString.substring(parenIndex + 1, pillarString.length - 1);
      const pinyinWords = pinyinPart.split(' ');
      const stemPinyin = pinyinWords[0];
      const branchPinyin = pinyinWords[1];
      return { stem: stemPinyin, branch: branchPinyin };
    } else if (pillarString.includes(' ')) {
      const parts = pillarString.split(' ');
      return { stem: parts[0], branch: parts[1] };
    } else {
      const stem = pillarString.charAt(0);
      const branch = pillarString.charAt(1);
      return { stem, branch };
    }
  };

  // const saveDataToProfile = async (formData, userId) => {
  //   try {
  //     const {
  //       birthDate, birthHour, birthMinute,
  //       city, gender, mbti, country
  //     } = formData;

  //     const fullBirthTimestamp = DateTime.fromISO(
  //       `${birthDate}T${String(birthHour).padStart(2, '0')}:${String(birthMinute).padStart(2, '0')}`
  //     ).toISO();

  //     const { error } = await supabase
  //       .from('USER_PROFILES')
  //       .update({
  //         date_of_birth: fullBirthTimestamp,
  //         city_of_birth: city || null,
  //         gender_at_birth: gender || null,
  //         mbti: mbti || null,
  //         country_of_birth: country || null
  //       })
  //       .eq('user_id', userId);

  //     if (error) console.error("Supabase Save Error:", error.message);
  //     else console.log("Profile updated with MBTI & Country!");
  //   } catch (err) {
  //     console.error("Error saving to profile:", err);
  //   }
  // };

  const parseStoredData = useCallback((storedData) => {
    try {
      const data = JSON.parse(storedData);
      return {
        trueSolarTime: data.trueSolarTime ? DateTime.fromISO(data.trueSolarTime, { setZone: true }) : null,
        baziPillars: data.baziPillars || null,
        prediction: data.prediction || "",
        language: data.language || "en",
        formData: data.formData || null,
        isLoading: data.isLoading || false
      };
    } catch (error) {
      console.error("Error parsing results data:", error);
      return null;
    }
  }, []);

  useEffect(() => {
    const performAnalysis = async () => {
      const storedData = sessionStorage.getItem('baziResults');
      
      if (!storedData) {
        setIsLoading(false);
        return;
      }

      const parsedData = parseStoredData(storedData);
      if (!parsedData) {
        setIsLoading(false);
        return;
      }

      // If data is already computed, just display it
      if (!parsedData.isLoading && parsedData.prediction) {
        setResultsData(parsedData);
        setIsLoading(false);
        return;
      }

      // Display BaZi pillars immediately (already computed)
      setResultsData(parsedData);
      setIsLoading(true); // Only for AI prediction

      try {
        const formData = parsedData.formData;
        if (!formData) {
          setIsLoading(false);
          return;
        }

        const lang = formData.language || parsedData.language;

        // BaZi pillars are already computed, parse them for API call
        const baziPillars = parsedData.baziPillars;
        const yearPillar = baziPillars ? parsePillar(baziPillars.yearPillar) : { stem: '', branch: '' };
        const monthPillar = baziPillars ? parsePillar(baziPillars.monthPillar) : { stem: '', branch: '' };
        const dayPillar = baziPillars ? parsePillar(baziPillars.dayPillar) : { stem: '', branch: '' };
        const hourPillar = baziPillars ? parsePillar(baziPillars.hourPillar) : { stem: '', branch: '' };

        // Call API for AI prediction
        let predictionResult = "";
        try {
          predictionResult = await analyzeBazi(
            yearPillar.stem, yearPillar.branch,
            monthPillar.stem, monthPillar.branch,
            dayPillar.stem, dayPillar.branch,
            hourPillar.stem, hourPillar.branch,
            lang
          );
        } catch (apiError) {
          console.error("API Error:", apiError);
          predictionResult = "Failed to get analysis from backend.";
        }

        // Update with final AI prediction
        const finalResultsData = {
          trueSolarTime: parsedData.trueSolarTime,
          baziPillars: parsedData.baziPillars,
          prediction: predictionResult,
          language: lang,
          isLoading: false,
          formData
        };

        sessionStorage.setItem('baziResults', JSON.stringify(finalResultsData));
        setResultsData({
          trueSolarTime: parsedData.trueSolarTime,
          baziPillars: parsedData.baziPillars,
          prediction: predictionResult,
          language: lang,
          formData
        });

      } catch (err) {
        console.error("Error during computation:", err);
        setResultsData(prev => ({
          ...prev,
          prediction: "Failed to compute analysis.",
          isLoading: false
        }));
      } finally {
        setIsLoading(false);
      }
    };

    performAnalysis();
  }, [parseStoredData, session]);

  const handleBackClick = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const { trueSolarTime, baziPillars, prediction } = resultsData;

  return (
    <div className="desti-container ethereal-theme">

      {/* --- 4. HEADER SECTION (Auth) --- */}
      <div className="auth-top" style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12, marginBottom: '1rem', width: '100%' }}>
        
        {/* Language Toggle */}
        <button
          className="lang-toggle auth-small-btn"
          onClick={() => {
            const next = language === "en" ? "zh" : "en";
            setLanguage(next);
            sessionStorage.setItem("desti_lang", next);
          }}
          style={{ marginRight: 0 }}
        >
          {language === "en" ? "中文" : "En"}
        </button>

        {/* AUTH BUTTONS */}
        {!session ? (
          <button className="auth-small-btn" onClick={() => setShowAuth(true)} aria-label="Sign in or Sign up">
            {language === "en" ? "Log In" : "登录"}
          </button>
        ) : (
          <div style={{ position: 'relative' }}>
            <button 
              className="avatar-btn" 
              onClick={() => setShowDropdown(!showDropdown)}
              title={session.user.email}
            >
              {(session.user.email || "U").charAt(0).toUpperCase()}
            </button>

            {showDropdown && (
              <div className="dropdown-menu">
                <div className="dropdown-header">
                  <span className="dropdown-user-email" style={{ fontSize: '0.9rem', color: '#333', fontWeight: 'bold' }}>
                    {session.user.email}
                  </span>
                </div>
                
                <button className="dropdown-item" onClick={() => navigate('/dashboard')}>
                  {language === "en" ? "📊 Dashboard" : "📊 仪表板"}
                </button>

                <button className="dropdown-item" onClick={() => navigate('/profile')}>
                  {language === "en" ? "👤 Profile" : "👤 个人资料"}
                </button>
                
                <div style={{ height: 1, background: 'rgba(0,0,0,0.05)', margin: '4px 0' }} />
                
                <button 
                  className="dropdown-item danger" 
                  onClick={() => {
                    setShowDropdown(false);
                    setShowLogoutConfirm(true);
                  }}
                >
                  {language === "en" ? "🚪 Log Out" : "🚪 退出登录"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {/* ------------------------------------------- */}

      {/* Back Button */}
      <button 
        onClick={handleBackClick}
        className="back-button"
        title={language === "zh" ? "返回" : "Back"}
        aria-label={language === "zh" ? "返回" : "Back"}
        style={{ marginTop: '10px' }} // Added a little margin since header is above it now
      >
        <img src={backArrowIcon} alt="Back" />
      </button>

      {/* Results Components */}
      <FortuneResult 
        prediction={prediction} 
        isLoading={isLoading} 
        language={language} 
      />

      <TSTnBaziResult
        trueSolarTime={trueSolarTime}
        baziPillars={baziPillars}
        isLoading={isLoading}
        language={language}
        gender={resultsData.formData?.gender}
      />

      {/* --- 5. AUTH POPUP MODAL --- */}
      {showAuth && <AuthPage onClose={() => setShowAuth(false)} />}

      {/* --- LOGOUT CONFIRMATION MODAL --- */}
      {showLogoutConfirm && (
        <div className="auth-modal-backdrop" onClick={() => setShowLogoutConfirm(false)} style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999
        }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white', padding: '25px', borderRadius: '8px',
              textAlign: 'center', minWidth: '300px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: '15px' }}>
              {language === "en" ? "Log Out?" : "确认登出?"}
            </h3>
            <p style={{ marginBottom: '25px', color: '#666' }}>
              {language === "en"
                ? "Are you sure you want to sign out?"
                : "您确定要退出登录吗?"}
            </p>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button
                onClick={() => setShowLogoutConfirm(false)}
                style={{
                  padding: '8px 20px', border: '1px solid #ccc',
                  backgroundColor: 'white',
                  color: '#333',
                  borderRadius: '4px', cursor: 'pointer'
                }}
              >
                {language === "en" ? "Cancel" : "取消"}
              </button>

              <button
                onClick={async () => {
                  await supabase.auth.signOut();
                  setShowLogoutConfirm(false);
                  navigate("/");
                }}
                style={{
                  padding: '8px 20px', border: 'none',
                  backgroundColor: '#dc3545', color: 'white',
                  borderRadius: '4px', cursor: 'pointer'
                }}
              >
                {language === "en" ? "Log Out" : "确认登出"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}